import { useCallback } from "react";
import { mutate } from "swr";
import wretch from "wretch";
import QueryStringAddon from "wretch/addons/queryString";
import { useAuthContext } from "../context/authContext";
import { useApiHeaders } from "../hooks/useApiHeaders";
import { withErrorBody } from "./errorBody";
import { getAccessDeniedReason } from "./types/accessControl";
import { FetcherOptions, PaginationQuery, PaginationQueryWithFields, UploadFileResponse } from "../types";

export type FileInfo = {
  data: string;
  contentType: string;
};

export const mutateCache = (keys: string[], skipKey?: string) => {
  for (const k of keys) {
    // SWR v2: mutate(key) revalida senza cancellare la cache corrente,
    // evitando che i componenti vedano data=undefined durante il refetch.
    // Questo previene flickering, smontaggio componenti e perdita di stato UI.
    mutate((key) => {
      if (typeof key === "string" && key !== skipKey) {
        return key === k || key.toLowerCase().startsWith(`${k}?`);
      }
      return false;
    });
  }
};

export const buildUrl = <T>(url: string, query: PaginationQuery | PaginationQueryWithFields<T>): string => {
  const params = new URLSearchParams();
  !!query.limit && params.set("limit", `${query.limit}`);
  !!query.page && params.set("page", `${query.page}`);
  !!query.q && params.set("q", query.q);
  !!query.orderBy && params.set("order_by", query.orderBy);
  query.desc != undefined && params.set("order", query.desc ? "desc" : "asc");
  query.filters?.length && params.set("filters", JSON.stringify(query.filters));

  if ("fields" in query) {
    query.fields?.length && params.set("fields", JSON.stringify(query.fields));
  }

  const op = url.includes("?") ? "&" : "?";

  return url + (params.size > 0 ? `${op}${params.toString()}` : "");
};

export const useApi = () => {
  const { expire, denyAccess } = useAuthContext();
  const apiHeaders = useApiHeaders();

  const handleError = useCallback(
    (error: any) => {
      if (error?.status === 401) expire();
      // Access denied while the user was inside (blocked, app locked or geo-blocked): take the whole
      // app down to the access-denied page instead of leaving each page to show its own 403
      // notification.
      const deniedReason = getAccessDeniedReason(error);
      if (deniedReason) denyAccess(deniedReason);
      throw error;
    },
    [expire, denyAccess],
  );

  const authenticatedClient = wretch()
    .addon(QueryStringAddon)
    .customError(withErrorBody)
    .options({ credentials: "include" })
    .headers(apiHeaders);

  const api = authenticatedClient.url("/api");

  const get = useCallback(
    <Result>({ url, signal }: FetcherOptions) =>
      api.options({ signal }).url(url).get().json<Result>().catch(handleError),
    [api, handleError],
  );

  const post = useCallback(
    <Result, Body>(url: string, body?: Body) => api.url(url).post(body).json<Result>().catch(handleError),
    [api, handleError],
  );

  const postNoResult = useCallback(
    <Body>(url: string, body?: Body) => api.url(url).post(body).res().catch(handleError),
    [api, handleError],
  );

  const put = useCallback(
    <Body>(url: string, body: Body) => api.url(url).put(body).res().catch(handleError),
    [api, handleError],
  );
  const putWithResult = useCallback(
    <Body, Result>(url: string, body: Body) => api.url(url).put(body).json<Result>().catch(handleError),
    [api, handleError],
  );

  const patch = useCallback(
    <Body>(url: string, body: Body) => api.url(url).patch(body).res().catch(handleError),
    [api, handleError],
  );

  const del = useCallback((url: string) => api.url(url).delete().res().catch(handleError), [api, handleError]);
  const delWithResult = useCallback(
    <Result>(url: string) => api.url(url).delete().json<Result>().catch(handleError),
    [api, handleError],
  );

  const uploadFile = useCallback(
    async (signedUrl: string, file: File): Promise<UploadFileResponse> => {
      try {
        const res = await fetch(signedUrl, {
          method: "PUT",
          headers: {
            "Content-Type": file.type,
            "Cache-Control": "private, max-age=0, no-transform",
          },
          body: file,
        });
        if (!res.ok) {
          throw new Error(`Upload failed: ${res.status}`);
        }
        return res as UploadFileResponse;
      } catch (error) {
        return handleError(error);
      }
    },
    [handleError],
  );

  const createObjectURL = useCallback(
    (url: string, redirectError?: boolean) =>
      authenticatedClient
        .options({ redirect: redirectError ? "error" : undefined })
        .url(url)
        .get()
        .blob()
        .then((blob) => URL.createObjectURL(blob))
        .catch(handleError),
    [authenticatedClient, handleError],
  );

  const downloadFile = useCallback(
    (url: string, fileName: string) =>
      createObjectURL(url, true)
        .then((res) => {
          const link = document.createElement("a");
          link.href = res;
          link.setAttribute("download", fileName);

          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
        })
        .catch(handleError),
    [createObjectURL, handleError],
  );

  const downloadExcel = useCallback(
    (url: string, fileName: string) =>
      api
        .headers({ Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
        .url(url)
        .get()
        .blob()
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);

          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
        })
        .catch(handleError),
    [api, handleError],
  );

  const getFileObjectUrl = useCallback(
    (url: string) =>
      api
        .url(url)
        .get()
        .json<FileInfo>()
        .then(({ data, contentType }) => {
          const byteCharacters = atob(data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const blob = new Blob([new Uint8Array(byteNumbers)], { type: contentType });
          return window.URL.createObjectURL(blob);
        })
        .catch(handleError),
    [api, handleError],
  );

  const postForFileObjectUrl = useCallback(
    <Body>(url: string, body: Body) =>
      api
        .url(url)
        .post(body)
        .json<FileInfo>()
        .then(({ data, contentType }) => {
          const byteCharacters = atob(data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const blob = new Blob([new Uint8Array(byteNumbers)], { type: contentType });
          return window.URL.createObjectURL(blob);
        })
        .catch(handleError),
    [api, handleError],
  );

  const postForFileDownload = useCallback(
    <Body>(url: string, body: Body, fileName: string, headers?: Record<string, string>) =>
      api
        .url(url)
        .headers(headers ?? {})
        .post(body)
        .json<FileInfo>()
        .then(({ data, contentType }) => {
          const byteCharacters = atob(data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const blob = new Blob([new Uint8Array(byteNumbers)], { type: contentType });
          const objectUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = objectUrl;
          link.setAttribute("download", fileName);

          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
          window.URL.revokeObjectURL(objectUrl);
        })
        .catch(handleError),
    [api, handleError],
  );

  return {
    get,
    post,
    postNoResult,
    put,
    putWithResult,
    patch,
    del,
    delWithResult,
    uploadFile,
    createObjectURL,
    downloadFile,
    downloadExcel,
    getFileObjectUrl,
    postForFileObjectUrl,
    postForFileDownload,
  };
};
